import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from '@hotjar/translations';

import { useCurrentUser } from '@hotjar/user';
import { show as showProductUpdates } from '@hotjar/webapp/features/ProductUpdates';
import { useUnseenHighlightsCount } from '@hotjar/webapp/features/Highlights/use-unseen-highlights-count';

import { Navigation } from '@hotjar/navigation';
import { createEmitter } from '@hotjar/navigation/src/events/createEmitter';

import { getRouteConfigByPath } from '@hotjar/webapp/utils/routing';
import type { NavigationProps } from '@hotjar/navigation/src/types';
import { useCurrentSiteId, useSites, useSite } from '@hotjar/site';
import * as hotjarTracking from '@hotjar/webapp/tracking/tools/hotjar';
import { trackNavigationClick } from '@hotjar/webapp/tracking/header';
import { noop } from '@hotjar/webapp/lib/function';
import { launchHelp } from '@hotjar/webapp/help/launch';
import { upsell as openUpsellModal } from '@hotjar/webapp/domains/revenue/upsell';
import { SITE_BAR_LINK } from '@hotjar/webapp/domains/revenue/constants/upsellReferrers';
import { isBillingV3Enabled, isZuoraEnabled } from '@hotjar/webapp/domains/billing/features';
import { isAllowed } from '@hotjar/webapp/domains/common/feature';

import { bus, currentSiteChanged } from '@hotjar/webapp/utils/events';

import { TopBarMessage } from '@hotjar/webapp/components/Sitebar';
import { SamplingWidget } from '@hotjar/webapp/features/SamplingWidget';
import { usePermission } from '@hotjar/webapp/domains/authorization';
import { useDemoSiteSelectorConfig } from '@hotjar/webapp/domains/activation/product-tour/';
import { useGettingStartedSideBar } from '@hotjar/webapp/features/Activation/GetStartedGuide/hooks/useGettingStartedSideBar';
import { useOptimizelyFeatureForUser } from '@hotjar/experimentation';
import { useFeatureFlag } from '@hotjar/webapp/hooks/useFeatureFlag';
import { SupportAccessModal } from './components/HelpButton/SupportAccessModal';
import { getOrgsAndSites } from './getOrgsAndSites';
import { useProductUpdatesCount } from './useProductUpdatesCount';

import { updateLocalStorageUserdata } from './components/SiteDropdown/utils';

type NewNavProps = Pick<NavigationProps, 'hideSidebar' | 'hideSiteWidgets'>;

export function NewNav({ hideSidebar, hideSiteWidgets }: NewNavProps): JSX.Element | null {
  const { data: currentUser } = useCurrentUser();
  const currentSiteId = useCurrentSiteId();
  const {
    query: { data: sites },
  } = useSites();
  const { data: currentSite } = useSite(Number(currentSiteId) ?? null);
  const { pathname } = useLocation();
  const [supportAccessModal, setSupportAccessModal] = useState({ isOpen: false, onSubmit: noop });
  const hasGrantSupportPermission = usePermission('org-grant-support-access');
  const hasEngageAccessTeam = usePermission('topspin-access-team');
  const hasEngageCreateTeam = usePermission('topspin-create-team');
  const hasEngageInsights = useFeatureFlag('engage_insights');
  const shouldShowExtendedLanguageSwitcher = isAllowed('localization-dev-mode', null);
  const { i18n } = useTranslation();
  const showPublicApiKeys = useOptimizelyFeatureForUser('sea_public_api_keys');

  const productUpdatesCount = useProductUpdatesCount(currentUser);

  const unseenHighlightsCount = useUnseenHighlightsCount(
    currentSite?.id ? `${currentSite.id}` : null,
  );

  const demoSiteSelectorConfig = useDemoSiteSelectorConfig();
  const gsgSideBarExperimentConfig = useGettingStartedSideBar();
  // endregion Hooks

  const routeConfig = getRouteConfigByPath(pathname);
  const showInvoices = !isZuoraEnabled(currentUser?.accounts?.[0]);

  const emitter = createEmitter();
  emitter.on('clickedHighlights', () => {
    unseenHighlightsCount.reset();
  });
  emitter.on('clickedProductUpdates', () => {
    showProductUpdates();
  });

  emitter.on('clickedUpgradeYourPlan', () => {
    if (!currentUser || !currentSite) {
      return;
    }

    openUpsellModal({
      site: currentSite,
      referrer: SITE_BAR_LINK,
    }).then(noop, noop);
  });

  emitter.on('selectedSite', ({ site }) => {
    if (!sites) {
      return;
    }

    updateLocalStorageUserdata(sites, site.id);

    bus.publish(currentSiteChanged());
  });

  emitter.on('clickedGetHelp', () => {
    if (!currentUser || !currentSite) {
      return;
    }

    launchHelp(
      hasGrantSupportPermission,
      currentUser?.id,
      currentSite,
      function requestSupportAccess(onAccessGranted) {
        setSupportAccessModal({ isOpen: true, onSubmit: onAccessGranted });
      },
      i18n?.resolvedLanguage ?? 'en',
    );
  });

  const closeModal = () =>
    setSupportAccessModal({
      onSubmit: noop,
      isOpen: false,
    });

  return currentUser && currentSite ? (
    <>
      <Navigation
        isAccountUpgradable={false}
        currentUser={currentUser}
        currentSite={currentSite}
        access={{
          canAccessInvoices: showInvoices,
          canAccessSubscriptions: isBillingV3Enabled(currentUser?.accounts?.[0]),
          canAccessPlansAndBilling: !isBillingV3Enabled(currentUser?.accounts?.[0]),
          canAccessEngageInsights: hasEngageInsights,
        }}
        hotjarTracking={hotjarTracking}
        trackClick={(name: string, position: string) =>
          trackNavigationClick(name, position, currentSite)
        }
        currentRouteConfig={routeConfig}
        emitter={emitter}
        hideSidebar={hideSidebar}
        hideSiteWidgets={hideSiteWidgets}
        getOrgsAndSites={getOrgsAndSites}
        sidebarNotifications={{
          highlights: `${unseenHighlightsCount.value || ''}`,
        }}
        whatsNewCount={productUpdatesCount || undefined}
        topBarMessage={
          currentSite?.effectivePlan ? (
            <TopBarMessage featureGroup={currentSite.effectivePlan} alternateStyling />
          ) : undefined
        }
        samplingWidget={<SamplingWidget />}
        hasEngageCreateTeam={hasEngageCreateTeam}
        hasEngageAccessTeam={hasEngageAccessTeam}
        shouldShowExtendedLanguageSwitcher={shouldShowExtendedLanguageSwitcher}
        demoSiteSelectorConfig={demoSiteSelectorConfig}
        gsgSideBarExperimentConfig={gsgSideBarExperimentConfig}
        showPublicApi={showPublicApiKeys}
      />
      {currentSite.account.id && (
        <SupportAccessModal
          isOpen={supportAccessModal.isOpen}
          onClose={closeModal}
          accountId={currentSite?.account.id}
          organizationId={currentSite?.organization.id}
          onSubmit={() => {
            supportAccessModal.onSubmit();
            closeModal();
          }}
        />
      )}
    </>
  ) : null;
}
